import { makeAutoObservable } from "mobx";
import { apiObjUrl } from "src/common/apiUrl";
import { delay } from "src/common/delay";
import { onError } from "src/common/onError";
import { rest } from "src/common/rest";
import { TableStore } from "src/components/tables/TableStore";
import { ZObjectItem } from "src/types/ZObjectItem";
import { zPersonEntity } from "src/types/ZPersonEntity";
import { zPersonSearchData } from "src/types/ZPersonSearchData";
import { z } from "zod";
import { ZRole } from "../../Obj2Tab/roles/roleTypes";

export const zPersonEntityNoRoles = zPersonEntity.omit({ roleIds: true });

export type ZPersonEntityNoRoles = z.infer<typeof zPersonEntityNoRoles>;

export type PersonRow = ZPersonEntityNoRoles;
type PersonFilters = {};

interface ProgressData {
  count: number;
  current: number; // 1-based
  percent: number;
  status: "active" | "exception" | "normal" | "success";
}

export class PersonDetailsStore {
  tableStore: TableStore<PersonRow, PersonFilters>;

  constructor(
    public role: ZRole,
    public roleObject: ZObjectItem,
  ) {
    this.tableStore = new TableStore<PersonRow, PersonFilters>({
      rowKey: "id",
      fnLoad: async ({ page, pageSize }) => {
        const body = {
          objectId: this.roleObject.id,
          stateId: "049d2c32-6d29-455c-bb25-f50d7afb5b35", // Алексей Жуков попросил заменить 1 на этот юид
        };
        const params = { page, size: pageSize, withLabels: true };
        const resp = await rest.post(apiObjUrl("/v2/users/search"), body, {
          params,
        });
        const res = zPersonSearchData.parse(resp.data);
        return {
          rows: res.content.map((item) => ({
            ...item,
            labelData: {
              labels: item.labelData?.labels || [],
              attributes: res.attributesLabels,
            },
          })),
          totalItems: res.totalElements,
        };
      },
      selectionSettings: { selectionType: "checkbox" },
    });
    makeAutoObservable(this);
  }

  get countToDelete(): number {
    return this.tableStore.selected.length;
  }

  get deleting(): boolean {
    return !!this.delProgress;
  }

  delProgress: ProgressData | null = null;

  setDelProgress(progress: ProgressData | null) {
    this.delProgress = progress;
  }

  async doDelete() {
    const selected = [...this.tableStore.selected];
    try {
      for (let i = 0; i < selected.length; i++) {
        this.setDelProgress({
          current: i + 1,
          count: selected.length,
          percent: Math.round((i * 100) / selected.length),
          status: "active",
        });
        const row = selected[i]!;
        // eslint-disable-next-line no-await-in-loop
        await rest.delete(apiObjUrl(`/users/${row.id}`));
      }
      this.setDelProgress({
        current: selected.length,
        count: selected.length,
        percent: 100,
        status: "success",
      });
    } catch (e) {
      onError(e);
      this.setDelProgress(
        this.delProgress && {
          ...this.delProgress,
          status: "exception",
        },
      );
    } finally {
      if (selected.length > 1) {
        await delay(500);
      }
      this.setDelProgress(null);
      this.tableStore.reload();
    }
  }
}
